<template>
  <div key="detail">
    <!-- Group header -->
    <div class="mb-6">
      <!-- Back + name row -->
      <div class="flex items-center gap-2 mb-1">
        <button
          class="w-7 h-7 rounded-full flex items-center justify-center text-[#7a9aaa] hover:text-[#0f1f2e] hover:bg-black/5 transition flex-shrink-0"
          :title="t('back')"
          @click="emit('back')"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 class="text-[20px] sm:text-[22px] font-semibold text-[#0f1f2e] tracking-tight leading-tight flex-1 min-w-0 truncate">
          {{ group.name }}
        </h2>
      </div>

      <!-- Meta -->
      <div class="flex items-center gap-2 pl-9 flex-wrap mb-3">
        <span class="text-[13px] text-[#7a9aaa]">{{ getDiseaseLabel(group.diseaseKey) }}</span>
        <span class="text-[#c0cdd4]">·</span>
        <span class="text-[13px] text-[#7a9aaa]">{{ group.memberCount }} {{ t("members") }}</span>
        <span
          v-if="!group.isPublic"
          class="inline-flex items-center gap-1 text-[10px] font-medium text-[#7a9aaa] bg-slate-100 rounded-full px-2 py-0.5"
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          {{ t("privateGroup") }}
        </span>
      </div>

      <!-- Actions row -->
      <div class="flex items-center gap-2 pl-9">
        <!-- Propose session: primary (dark) — always visible, login check in handler -->
        <button
          class="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-full bg-[#0f1f2e] text-white hover:opacity-85 transition"
          @click="emit('propose-session')"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ t("proposeSession") }}
        </button>
        <!-- Invite: secondary (border) -->
        <button
          class="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-full bg-[#4885d0] text-white hover:opacity-85 transition"
          @click="emit('share-detail')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          {{ t("inviteToGroup") }}
        </button>
      </div>

    </div>

    <!-- Loading -->
    <div v-if="loadingSessions" class="flex justify-center py-16">
      <div class="w-6 h-6 rounded-full border-2 border-[#0f1f2e] border-t-transparent animate-spin" />
    </div>

    <!-- Sessions list -->
    <div v-else-if="sessions.length > 0" class="flex flex-col gap-3">
      <div
        v-for="session in sessions"
        :key="session.id"
        class="px-5 py-4 rounded-2xl border backdrop-blur shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
        :class="
          session.status === 'cancelled' || isSessionPast(session.dateTime)
            ? 'opacity-50 border-black/5 bg-white/40'
            : 'border-white/90 bg-white/70'
        "
      >
        <div class="flex gap-4">
          <!-- Date box -->
          <div
            class="flex-shrink-0 w-[46px] rounded-xl overflow-hidden shadow-sm border border-black/10 text-center select-none self-start"
          >
            <div
              class="bg-[#0f1f2e] text-white text-[9px] font-semibold uppercase tracking-wider py-[3px]"
            >
              {{ formatDayOfWeek(session.dateTime) }}
            </div>
            <div class="bg-white pt-1.5 pb-1 flex flex-col items-center">
              <span
                class="text-[22px] font-bold text-[#0f1f2e] leading-none"
                >{{ formatDay(session.dateTime) }}</span
              >
              <span
                class="text-[8px] font-semibold uppercase tracking-wider text-[#7a9aaa] mt-0.5"
                >{{ formatMonthAbbr(session.dateTime) }}</span
              >
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 flex flex-col gap-2.5">
            <!-- Row 1: title + status as plain text right-aligned -->
            <div class="flex items-center justify-between gap-3">
              <span
                class="text-[15px] font-semibold text-[#0f1f2e] tracking-tight leading-snug"
              >
                {{ formatWeekdayTime(session.dateTime) }}
              </span>
              <span
                v-if="isSessionPast(session.dateTime)"
                class="text-[12px] font-semibold text-[#9ab4c0] flex-shrink-0"
              >{{ t("sessionPast") }}</span>
              <span
                v-else-if="session.status !== 'open'"
                class="flex items-center gap-1.5 flex-shrink-0"
                :class="
                  session.status === 'confirmed'
                    ? 'text-[#1a3a6e]'
                    : 'text-slate-400'
                "
              >
                <span
                  class="w-[7px] h-[7px] rounded-full flex-shrink-0"
                  :class="
                    session.status === 'confirmed'
                      ? 'bg-[#9dc0fd]'
                      : 'bg-slate-300'
                  "
                />
                <span class="text-[14px] font-semibold">
                  {{
                    session.status === "confirmed"
                      ? t("sessionConfirmed")
                      : t("sessionCancelled")
                  }}
                </span>
              </span>
            </div>

            <!-- Row 2: host + attendees with 2-slot progress -->
            <div class="flex items-center gap-4 flex-wrap">
              <span
                class="flex items-center gap-1.5 text-[14px] font-medium text-[#3d5a6e]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                </svg>
                {{ session.hostName }}
              </span>
              <span
                class="flex items-center gap-1.5 text-[14px] text-[#3d5a6e]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span class="font-semibold">{{
                  session.attendeeCount
                }}</span>
                {{ t("attendees") }}
                <span
                  v-if="session.status === 'open'"
                  class="text-[#b0c4d4]"
                  >· {{ t("neededToConfirm") }}</span
                >
              </span>
              <!-- Spots left chip -->
              <span
                v-if="session.maxAttendees != null"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-semibold"
                :class="session.spotsLeft === 0
                  ? 'bg-red-50 text-red-500'
                  : session.spotsLeft != null && session.spotsLeft <= 3
                    ? 'bg-amber-50 text-amber-600'
                    : 'bg-slate-100 text-[#3d5a6e]'"
              >
                {{ session.spotsLeft === 0 ? t("sessionFull") : `${session.attendeeCount} / ${session.maxAttendees}` }}
              </span>
            </div>

            <!-- Description (optional) -->
            <div v-if="session.description">
              <p
                class="text-[13.5px] text-[#3d5a6e] leading-relaxed"
                :class="
                  expandedSessionId !== session.id ? 'line-clamp-2' : ''
                "
              >
                {{ session.description }}
              </p>
              <button
                v-if="session.description.length > 100"
                class="text-[12px] font-semibold text-[#9dc0fd] hover:text-[#1a3a6e] transition mt-0.5"
                @click="emit('expand-session', session.id)"
              >
                {{
                  expandedSessionId === session.id
                    ? t("viewLess")
                    : t("viewMore")
                }}
              </button>
            </div>

            <!-- Row 3: actions, separated by a thin line -->
            <div
              v-if="
                session.status !== 'cancelled' &&
                !isSessionPast(session.dateTime)
              "
              class="flex items-center gap-2 flex-wrap pt-2.5 border-t border-black/[0.06]"
            >
              <!-- Join call (only near start) -->
              <a
                v-if="
                  session.status === 'confirmed' &&
                  session.jitsiLink &&
                  session.isAttending &&
                  isCallActive(session.dateTime)
                "
                :href="`/session/${session.accessSlug ?? session.id}`"
                target="_blank"
                class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0f1f2e] bg-[#9dc0fd] px-4 py-2 rounded-full hover:opacity-85 transition"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polygon points="23 7 16 12 23 17" />
                  <rect x="1" y="5" width="15" height="14" rx="2" />
                </svg>
                {{ t("joinCall") }}
              </a>

              <!-- Single invite button -->
              <button
                class="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-full bg-[#4885d0] text-white hover:opacity-85 transition"
                @click="emit('share-session', session)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                {{ t("invitePeople") }}
              </button>

              <!-- Join / Leave — right-aligned -->
              <button
                class="ml-auto text-[13px] font-semibold px-4 py-2 rounded-full transition whitespace-nowrap"
                :class="
                  session.isAttending
                    ? 'border border-red-200 text-red-400 hover:border-red-300 hover:bg-red-50'
                    : 'bg-[#0f1f2e] text-white hover:opacity-85'
                "
                @click="emit('toggle-session', session)"
              >
                {{ session.isAttending ? t("unsignUp") : t("signUp") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="rounded-[18px] border border-white/90 bg-white/60 backdrop-blur-2xl shadow-[0_2px_24px_rgba(0,0,0,0.05)] px-6 py-14 text-center"
    >
      <div
        class="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ab4c0"
          stroke-width="1.5"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="16" y1="2" x2="16" y2="6" />
        </svg>
      </div>
      <p class="text-[#7a9aaa] mb-3">{{ t("noSessions") }}</p>
      <button
        class="font-semibold text-[#0f1f2e] underline hover:no-underline transition"
        @click="emit('propose-session')"
      >
        {{ t("proposeFirst") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Group, Session } from "@/types/domain";
import { useLiveGroups } from "@/components/companionGroups/useLiveGroups";

defineProps<{
  group: Group;
  sessions: Session[];
  loadingSessions: boolean;
  expandedSessionId: string | null;
  memberGroupIds: Set<string>;
  isLoggedIn?: boolean;
  sessionLinkCopied: string | null;
  detailLinkCopied: boolean;
}>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "propose-session"): void;
  (e: "toggle-session", session: Session): void;
  (e: "expand-session", sessionId: string): void;
  (e: "share-session", session: Session): void;
  (e: "share-detail"): void;
  (e: "whatsapp-detail"): void;
  (e: "telegram-detail"): void;
  (e: "copy-detail"): void;
}>();

const {
  t,
  getDiseaseLabel,
  formatDay,
  formatMonthAbbr,
  formatDayOfWeek,
  formatWeekdayTime,
  isSessionPast,
  isCallActive,
} = useLiveGroups(
  { _skipInit: true },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  (_e: any, ..._args: any[]) => {},
);
</script>
